"use client"

import type React from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Card } from "../ui/card-animated"

import { ToggleSwitch } from "../ui/toggle-switch-animated"
import type { FormData, AlcoholOption } from "./types"
import { TextInput } from "../ui/text-input-animated"

interface DetailedHealthQuestionnaireSectionProps {
  formData: FormData
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  optionsAlcohol: AlcoholOption[]
  conditionalTextareaClass: string
}

const slideUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
}

export const DetailedHealthQuestionnaireSection: React.FC<DetailedHealthQuestionnaireSectionProps> = ({
  formData,
  handleChange,
  optionsAlcohol,
  conditionalTextareaClass,
}) => {
  return (
    <Card title="Cuestionario de Salud Detallado" delay={7} isCollapsible={true} isInitiallyCollapsed={true}>
      <div className="space-y-4">
        {/* Sección Fumador, Alcohol, Sueño */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Es fumador?</label>
              <div className="w-20">
                <ToggleSwitch name="fumador" checked={formData.fumador} onChange={handleChange} />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Consume alcohol con frecuencia?</label>
              <div className="w-40">
                {/* <SelectInput
                  label=""
                  name="selectedAlcohol"
                  value={formData.selectedAlcohol}
                  onChange={handleChange}
                  options={optionsAlcohol}
                /> */}
              </div>
            </motion.div>
          </div>
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">Horas de sueño</label>
              <div className="w-20">
                <TextInput
                  label=""
                  type="number"
                  name="hours_sleep"
                  value={formData.hours_sleep}
                  onChange={handleChange}
                />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Duerme bien?</label>
              <div className="w-20">
                <ToggleSwitch name="dormirBien" checked={formData.dormirBien} onChange={handleChange} />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Sección Medicamentos, Problema Médico, Analítica, Tensión */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Toma medicamentos?</label>
              <div className="w-20">
                <ToggleSwitch name="tomarMedicamentos" checked={formData.tomarMedicamentos} onChange={handleChange} />
              </div>
            </motion.div>
            <AnimatePresence>
              {formData.tomarMedicamentos && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-3 mb-5 overflow-hidden"
                >
                  <TextInput
                    label="Descripción de medicamentos"
                    name="medication_description"
                    value={formData.medication_description}
                    onChange={handleChange}
                    isTextarea
                    customClassName={conditionalTextareaClass}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene algún problema médico?</label>
              <div className="w-20">
                <ToggleSwitch name="problemaMedico" checked={formData.problemaMedico} onChange={handleChange} />
              </div>
            </motion.div>
            <AnimatePresence>
              {formData.problemaMedico && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-3 mb-5 overflow-hidden"
                >
                  <TextInput
                    label="Descripción del problema médico"
                    name="medical_problem_description_detailed"
                    value={formData.medical_problem_description_detailed}
                    onChange={handleChange}
                    isTextarea
                    customClassName={conditionalTextareaClass}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">
                ¿Su última analítica presentó alguna alteración?
              </label>
              <div className="w-20">
                <ToggleSwitch name="analiticaAlterada" checked={formData.analiticaAlterada} onChange={handleChange} />
              </div>
            </motion.div>
            <AnimatePresence>
              {formData.analiticaAlterada && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-3 mb-5 overflow-hidden"
                >
                  <TextInput
                    label="Descripción de la alteración"
                    name="last_analytic_altered_description"
                    value={formData.last_analytic_altered_description}
                    onChange={handleChange}
                    isTextarea
                    customClassName={conditionalTextareaClass}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene la tensión arterial alterada?</label>
              <div className="w-20">
                <ToggleSwitch name="presionAlterada" checked={formData.presionAlterada} onChange={handleChange} />
              </div>
            </motion.div>
            <AnimatePresence>
              {formData.presionAlterada && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-3 mb-5 overflow-hidden"
                >
                  <TextInput
                    label="Descripción de la alteración"
                    name="altered_tension_description"
                    value={formData.altered_tension_description}
                    onChange={handleChange}
                    isTextarea
                    customClassName={conditionalTextareaClass}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Sección Ciclo Menstrual, Riesgo, Lesiones Graves */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Su ciclo menstrual se ve afectado?</label>
              <div className="w-20">
                <ToggleSwitch name="affectPeriod" checked={formData.affectPeriod} onChange={handleChange} />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene amenorrea?</label>
              <div className="w-20">
                <ToggleSwitch name="amenorrea" checked={formData.amenorrea} onChange={handleChange} />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene el periodo irregular?</label>
              <div className="w-20">
                <ToggleSwitch name="irregulaPeriod" checked={formData.irregulaPeriod} onChange={handleChange} />
              </div>
            </motion.div>
            <AnimatePresence>
              {formData.irregulaPeriod && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-3 mb-5 overflow-hidden"
                >
                  <TextInput
                    label="Frecuencia del periodo irregular"
                    name="irregular_period_frequency"
                    value={formData.irregular_period_frequency}
                    onChange={handleChange}
                    isTextarea
                    customClassName={conditionalTextareaClass}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Se considera en estado de riesgo?</label>
              <div className="w-20">
                <ToggleSwitch name="riskStatus" checked={formData.riskStatus} onChange={handleChange} />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Ha sufrido lesiones graves detalladas?</label>
              <div className="w-20">
                <ToggleSwitch
                  name="seriousInjuries_detailed"
                  checked={formData.seriousInjuries_detailed}
                  onChange={handleChange}
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Sección Lesiones Específicas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene lesiones en el tobillo?</label>
              <div className="w-20">
                <ToggleSwitch name="ankle" checked={formData.ankle} onChange={handleChange} />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene lesiones en la rodilla?</label>
              <div className="w-20">
                <ToggleSwitch name="knee" checked={formData.knee} onChange={handleChange} />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene lesiones en la cadera?</label>
              <div className="w-20">
                <ToggleSwitch name="hip" checked={formData.hip} onChange={handleChange} />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene lesiones en el codo?</label>
              <div className="w-20">
                <ToggleSwitch name="elbow" checked={formData.elbow} onChange={handleChange} />
              </div>
            </motion.div>
          </div>
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene lesiones lumbares?</label>
              <div className="w-20">
                <ToggleSwitch name="lumbar" checked={formData.lumbar} onChange={handleChange} />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene lesiones cervicales?</label>
              <div className="w-20">
                <ToggleSwitch name="cervical" checked={formData.cervical} onChange={handleChange} />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene lesiones en el deltoides?</label>
              <div className="w-20">
                <ToggleSwitch name="deltoid" checked={formData.deltoid} onChange={handleChange} />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene lesiones en la mano?</label>
              <div className="w-20">
                <ToggleSwitch name="hand" checked={formData.hand} onChange={handleChange} />
              </div>
            </motion.div>
          </div>
        </div>
        <motion.div variants={slideUp} className="mb-4">
          <TextInput
            label="Otras lesiones"
            name="other_injuries"
            value={formData.other_injuries}
            onChange={handleChange}
            isTextarea
          />
        </motion.div>

        {/* Sección Actividad Física */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Cuántas horas al día está sentado?</label>
              <div className="w-20">
                <TextInput
                  label=""
                  type="number"
                  name="sitting_hours"
                  value={formData.sitting_hours}
                  onChange={handleChange}
                />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Realiza actividad física?</label>
              <div className="w-20">
                <ToggleSwitch name="activtyFisical" checked={formData.activtyFisical} onChange={handleChange} />
              </div>
            </motion.div>
            <AnimatePresence>
              {formData.activtyFisical && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-3 mb-5 overflow-hidden"
                >
                  <TextInput
                    label="Descripción de la actividad física"
                    name="physical_activity_description"
                    value={formData.physical_activity_description}
                    onChange={handleChange}
                    isTextarea
                    customClassName={conditionalTextareaClass}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Cuántos días a la semana entrena?</label>
              <div className="w-20">
                <TextInput
                  label=""
                  type="number"
                  name="days_week_train"
                  value={formData.days_week_train}
                  onChange={handleChange}
                />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene experiencia de entrenamiento?</label>
              <div className="w-20">
                <TextInput
                  label=""
                  type="text"
                  name="training_experience"
                  value={formData.training_experience}
                  onChange={handleChange}
                />
              </div>
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Entrena con entrenador?</label>
              <div className="w-20">
                <TextInput
                  label=""
                  type="text"
                  name="training_with_trainer"
                  value={formData.training_with_trainer}
                  onChange={handleChange}
                />
              </div>
            </motion.div>
          </div>
        </div>
        <motion.div variants={slideUp} className="mb-4">
          <TextInput
            label="Describa su entrenamiento semanal"
            name="describe_week_training"
            value={formData.describe_week_training}
            onChange={handleChange}
            isTextarea
          />
        </motion.div>
        <motion.div variants={slideUp} className="mb-4">
          <TextInput
            label="¿Qué mejoras busca?"
            name="improvements"
            value={formData.improvements}
            onChange={handleChange}
            isTextarea
          />
        </motion.div>

        {/* Sección Equipamiento Gimnasio, Hora Entrenamiento */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Su gimnasio está bien equipado?</label>
              <div className="w-20">
                <ToggleSwitch name="equipeGym" checked={formData.equipeGym} onChange={handleChange} />
              </div>
            </motion.div>
            <AnimatePresence>
              {formData.equipeGym && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-3 mb-5 overflow-hidden"
                >
                  <TextInput
                    label="Descripción del equipamiento del gimnasio"
                    name="well_equipped_gym_description"
                    value={formData.well_equipped_gym_description}
                    onChange={handleChange}
                    isTextarea
                    customClassName={conditionalTextareaClass}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">
                ¿Cuál es su hora habitual de entrenamiento?
              </label>
              <div className="w-20">
                <TextInput
                  label=""
                  type="text"
                  name="usual_training_time"
                  value={formData.usual_training_time}
                  onChange={handleChange}
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Sección Alimentación */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <motion.div variants={slideUp} className="mb-4">
              <TextInput
                label="Alimentos que no le gustan"
                name="foods_not_like"
                value={formData.foods_not_like}
                onChange={handleChange}
                isTextarea
              />
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Tiene alguna intolerancia alimentaria?</label>
              <div className="w-20">
                <ToggleSwitch name="intolerance" checked={formData.intolerance} onChange={handleChange} />
              </div>
            </motion.div>
            <AnimatePresence>
              {formData.intolerance && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-3 mb-5 overflow-hidden"
                >
                  <TextInput
                    label="Descripción de la intolerancia"
                    name="intolerance_description"
                    value={formData.intolerance_description}
                    onChange={handleChange}
                    isTextarea
                    customClassName={conditionalTextareaClass}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div>
            <motion.div variants={slideUp} className="mb-4">
              <TextInput
                label="Preferencias alimentarias"
                name="food_preferences"
                value={formData.food_preferences}
                onChange={handleChange}
                isTextarea
              />
            </motion.div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Cuántas comidas al día realiza?</label>
              <div className="w-20">
                <TextInput
                  label=""
                  type="number"
                  name="how_may_meal_day"
                  value={formData.how_may_meal_day}
                  onChange={handleChange}
                />
              </div>
            </motion.div>
          </div>
        </div>
        <motion.div variants={slideUp} className="mb-4">
          <TextInput
            label="¿Qué comidas realiza en casa y fuera?"
            name="meals_in_home_and_outside"
            value={formData.meals_in_home_and_outside}
            onChange={handleChange}
            isTextarea
          />
        </motion.div>
        <motion.div variants={slideUp} className="mb-4">
          <TextInput
            label="¿Qué alimentos consume en casa y fuera?"
            name="foods_in_home_and_outside"
            value={formData.foods_in_home_and_outside}
            onChange={handleChange}
            isTextarea
          />
        </motion.div>

        {/* Sección Suplementación */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Toma suplementación?</label>
              <div className="w-20">
                <ToggleSwitch name="supplementation" checked={formData.supplementation} onChange={handleChange} />
              </div>
            </motion.div>
            <AnimatePresence>
              {formData.supplementation && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-3 mb-5 overflow-hidden"
                >
                  <TextInput
                    label="Descripción de la suplementación"
                    name="supplementation_description"
                    value={formData.supplementation_description}
                    onChange={handleChange}
                    isTextarea
                    customClassName={conditionalTextareaClass}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div>
            <motion.div variants={slideUp} className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium pr-3 text-sm">¿Utiliza suplementación?</label>
              <div className="w-20">
                <ToggleSwitch name="useSupplementation" checked={formData.useSupplementation} onChange={handleChange} />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Card>
  )
}
